import React from 'react';
import { getPSPLandingViewPixel } from '@sparefoot/react-core/utils/tracking';

import { Page, Section, Container, Group } from 'components/core/Page';
import {
	H3,
	Paragraph,
	UnorderedList,
	ListItem,
} from 'components/core/Typography';
import { SearchHero } from 'components/landing/SearchHero';

import carAndBikeImage1 from 'assets/images/landing/vehicle/carandbike-storage--outdoor.jpg';
import carAndBikeImage2 from 'assets/images/landing/vehicle/carandbike-storage--covered.jpg';
import carAndBikeImage3 from 'assets/images/landing/vehicle/carandbike-storage--indoor.jpg';
import carAndBikeImage4 from 'assets/images/landing/vehicle/carandbike-storage--motorcycle.jpg';

import watercraftImage1 from 'assets/images/landing/vehicle/watercraft-storage--indoor.jpg';
import watercraftImage2 from 'assets/images/landing/vehicle/watercraft-storage--outdoor.jpg';
import watercraftImage3 from 'assets/images/landing/vehicle/watercraft-storage--jet-ski.jpg';

import rvImage1 from 'assets/images/landing/vehicle/rv-storage--covered.jpg';
import rvImage2 from 'assets/images/landing/vehicle/rv-storage--indoor.jpg';
import rvImage3 from 'assets/images/landing/vehicle/rv-storage--outdoor.jpg';
import rvImage4 from 'assets/images/landing/vehicle/rv-storage--parking.jpg';

import './VehicleStoragePage.scss';
import { INTERNAL_PAGE_TYPES } from '../../config/constants';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useCanonical from 'hooks/useCanonical';

export function VehicleStoragePage() {
	const canonical = useCanonical('/vehicle-storage');
	usePageViewTracking(INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE);
	return (
		<Page
			className="vehicle-landing-page"
			trackingPixel={getPSPLandingViewPixel('vehicle')}
			pageType={INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE}
			title="Vehicle Storage: Indoor & Outdoor Storage Options - SelfStorage.com"
			description="Find cheap parking and storage for your car, boat, RV, motorcycle and more. With over 40,000 facilities, we make it easy to find the best unit for storing your vehicle."
			canonical={canonical}
		>
			<SearchHero
				headline="Need storage for vehicles?"
				subheading="Self-storage is perfect for off-season car, RV, boat and bike storage."
				className="vehicle-search-hero"
				withSearchType={false}
				additionalSearchParams={{
					type: 'vehicle',
				}}
			/>
			<Section>
				<Container>
					<H3 size="large">Why choose vehicle self-storage?</H3>
					<Paragraph>
						Proper long-term vehicle storage means more than simply
						letting it collect dust in a stuffy garage. The benefits
						of vehicle self-storage include:
					</Paragraph>
					<UnorderedList className="with-bullets">
						<ListItem>
							Superior protection from the elements
						</ListItem>
						<ListItem>
							Convenient move-in, move-out and accessibility
						</ListItem>
						<ListItem>
							Ultimate flexibility: Choose from indoor, outdoor,
							covered or uncovered vehicle storage options
						</ListItem>
					</UnorderedList>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">
						What are average vehicle storage prices?
					</H3>
					<Paragraph>
						Pricing will depend on the type of storage space you
						choose. There are three types of vehicle storage
						options:
					</Paragraph>
					<UnorderedList>
						<ListItem>
							Uncovered parking space (least expensive): An open
							area where you can safely leave the vehicle. Least
							expensive.
						</ListItem>
						<ListItem>
							Covered parking space (mid-range): Outdoor area with
							a roof (similar to a carport).
						</ListItem>
						<ListItem>
							Indoor unit (most expensive): An enclosed unit with
							a roll-up door that can be locked. Best for smaller
							vehicles.
						</ListItem>
					</UnorderedList>
				</Container>
			</Section>
			<Section>
				<Container>
					<Group>
						<H3 size="medium">Car and motorcycle storage</H3>
						<Paragraph>
							Self-storage facilities across the nation have
							stepped up their car storage offerings in the past
							decade. With a variety of amenities and protection
							options, car storage units are an unbeatable way to
							make sure your vehicle gets the space it deserves.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={carAndBikeImage1}
							alt="outdoor car storage"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Outdoor car storage</H3>
						<Paragraph>
							These range from paved parking lot storage spots to
							unused space around the facility’s premises. Because
							these spots are typically uncovered, they are also
							the cheapest option. We do not advise using outdoor
							car storage if you live in an area prone to violent
							weather.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={carAndBikeImage2}
							alt="covered car storage"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Covered car storage</H3>
						<Paragraph>
							Some outdoor car storage spots have canopies under
							which you can park. These spots cost more than
							regular uncovered outdoor spaces, but will give your
							vehicle extra protection from rain, hail, dust and
							dirt.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={carAndBikeImage3}
							alt="indoor car storage"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Indoor car storage</H3>
						<Paragraph>
							Need long-term storage for your car? Indoor
							self-storage units are your best bet for safe,
							secure storage. Smaller vehicles may be able to fit
							in a 10x15, while most vehicles will fit comfortably
							in a 10x30. Note that indoor car storage is the most
							expensive option.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={carAndBikeImage4}
							alt="motorcycle storage"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Motorcycle storage</H3>
						<Paragraph>
							Motorcycles will fit in traditional self-storage
							units that run at least 10 feet deep.
						</Paragraph>
					</Group>
				</Container>
			</Section>
			<Section>
				<Container>
					<Group>
						<H3 size="medium">
							Boat, jet ski and watercraft storage
						</H3>
						<Paragraph>
							Don’t want to pay extra for marina storage?
							Self-storage units can be an extremely
							cost-effective alternative.
						</Paragraph>
						<Paragraph>
							Size is the most important factor to consider when
							choosing boat storage.
						</Paragraph>
						<UnorderedList>
							<ListItem>
								Most boats will fit in a 10x15 storage unit.
							</ListItem>
							<ListItem>
								Larger boats may need a 10x20 or 10x30 storage
								unit.
							</ListItem>
							<ListItem>
								Note that storage units have heights ranging
								from eight to ten feet.
							</ListItem>
						</UnorderedList>
					</Group>
					<Group>
						<img
							src={watercraftImage1}
							alt="indoor boat storage unit"
							className="landing-image"
							width="390"
							height="120"
						/>
						<H3 size="small">Indoor boat storage</H3>
						<Paragraph>
							If you opt for indoor boat storage, keep in mind
							that prices will be significantly higher than
							outdoor boat storage spaces.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={watercraftImage2}
							alt="outdoor boat storage unit"
							className="landing-image"
							width="390"
							height="120"
						/>
						<H3 size="small">
							Outdoor boat storage at a storage facility
						</H3>
						<Paragraph>
							These are outdoor lots on the storage facility
							property where you can safely park your boat. Some
							facilities offer both covered and uncovered outdoor
							boat storage options.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={watercraftImage3}
							alt="indoor jet ski storage unit"
							className="landing-image"
							width="390"
							height="120"
						/>
						<H3 size="small">Jet ski storage</H3>
						<Paragraph>
							Traditional self-storage units are perfect for jet
							ski storage. Most jet skis will be able to fit in a
							10x10 storage unit.
						</Paragraph>
					</Group>
				</Container>
			</Section>

			<Section>
				<Container>
					<Group>
						<H3 size="medium">RV storage</H3>
						<Paragraph>
							Many storage facilities also offer RV storage. While
							most RV spaces will be either outdoor covered or
							uncovered, some storage facilities may have enough
							space for indoor RV storage.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={rvImage1}
							alt="outdoor RV storage lot"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">RV outdoor lot storage</H3>
						<Paragraph>
							RV outdoor storage spaces will usually be unused,
							open spots on the facility’s property. These spots
							are often uncovered (meaning your vehicle won’t be
							protected from the elements). Outdoor lot storage is
							the least expensive option for RV storage.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={rvImage2}
							alt="outdoor RV parking"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">RV storage parking spaces</H3>
						<Paragraph>
							Unlike outdoor lot spaces, RV parking spaces are
							paved. Some facilities will mark specific areas off
							for RVs. Although RV parking spaces are also
							uncovered, the paved surface can do a bit more to
							protect your RV from dirt and dust (versus an open
							lot).
						</Paragraph>
					</Group>
					<Group>
						<img
							src={rvImage3}
							alt="covered RV storage lot"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Covered RV storage</H3>
						<Paragraph>
							While a more expensive option, covered RV storage
							protects your RV from the elements—ideal if you live
							in an area with severe weather conditions.
						</Paragraph>
					</Group>
					<Group>
						<img
							src={rvImage4}
							alt="indoor RV storage lot"
							className="landing-image"
							width="290"
							height="120"
						/>
						<H3 size="small">Indoor RV storage units</H3>
						<Paragraph>
							These are large versions of typical self-storage
							units, and the safest way to store your vehicle.
							Indoor RV storage units usually clock in at 12-15
							ft. wide, 30-50 ft. long and 15 ft. tall. Since
							these indoor units are non-negotiable in space, you
							must make sure your RV will be able to fit. Indoor
							RV storage units are the most expensive amongst all
							RV storage options.
						</Paragraph>
					</Group>
				</Container>
			</Section>

			<Section>
				<Container>
					<Group>
						<H3 size="medium">
							What storage unit size do I need for my vehicle?
						</H3>
					</Group>
					<Group>
						<H3 size="small">10x10 storage units</H3>
						<UnorderedList>
							<ListItem>
								Ideal for motorcycles and small cars
							</ListItem>
							<ListItem>May fit some jet skis</ListItem>
						</UnorderedList>
					</Group>
					<Group>
						<H3 size="small">10x15 storage units</H3>
						<UnorderedList>
							<ListItem>
								Fits most coupes, hatchbacks, crossovers and
								small SUVs
							</ListItem>
							<ListItem>Jet skis will fit comfortably</ListItem>
							<ListItem>
								May be a tight fit for standard boats
							</ListItem>
						</UnorderedList>
					</Group>
					<Group>
						<H3 size="small">10x20 storage units</H3>
						<UnorderedList>
							<ListItem>
								Most cars will fit comfortably in this unit size
							</ListItem>
							<ListItem>Good fit for most boats</ListItem>
						</UnorderedList>
					</Group>
					<Group>
						<H3 size="small">10x30 storage units</H3>
						<UnorderedList>
							<ListItem>
								Perfect for larger trucks and vans. Most storage
								units have a ceiling height of eight to ten
								feet.
							</ListItem>
							<ListItem>Good fit for most boats</ListItem>
						</UnorderedList>
					</Group>
				</Container>
			</Section>

			<Section>
				<Container>
					<Group>
						<H3 size="medium">Vehicle storage guidelines</H3>
						<Paragraph>
							To ensure a safe and seamless experience, be sure
							you follow these guidelines when storing your
							vehicle:
						</Paragraph>
					</Group>
					<Group>
						<UnorderedList>
							<ListItem>
								Bring your vehicle registration and proof of
								insurance
							</ListItem>
							<ListItem>
								All vehicles must have current license plates
							</ListItem>
							<ListItem>
								If storing long-term, remove the battery and
								store it in a cool, dry place
							</ListItem>
							<ListItem>
								Make sure your vehicle is ready for storage.
								This may include cleaning the water tanks,
								removing gas from the gas tank, filling up the
								tires, checking fuel lines, buying car covers,
								etc.
							</ListItem>
						</UnorderedList>
					</Group>
					<Paragraph>
						Some requirements may vary by facility—always contact
						your store manager for details.
					</Paragraph>
				</Container>
			</Section>
		</Page>
	);
}

export default VehicleStoragePage;
